import React, { useEffect, useState } from "react";

export const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const NAME_RE = /^\p{L}[\p{L}\p{M}\s.'\u2019-]*$/u;
export const NAME_MAX = 255;
export const FONT = "Outfit, sans-serif";
export const GRADIENT = "linear-gradient(100deg,#C95EBE,#8B55DD)";
export const AVATAR_GRADIENT = "linear-gradient(140deg,#C95EBE,#8B55DD)";

export const fieldHeight = (mobile, short, roomy) => (mobile ? 50 : roomy ? 56 : short ? 48 : 54);

export const isValidName = (name) => {
  const nm = name.trim();
  return nm.length >= 2 && nm.length <= NAME_MAX && NAME_RE.test(nm);
};

export const nameParts = (name, email) => {
  const nm = name.trim();
  const first = nm ? nm.split(/\s+/)[0] : email ? email.split("@")[0] : "there";
  const initials = nm
    ? nm.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : first.slice(0, 1).toUpperCase();
  return { first, initials };
};

export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

const BORDERS = {
  bad: { border: "1.5px solid #E0476B", boxShadow: "0 0 0 3px rgba(224,71,107,.12)" },
  focus: { border: "1.5px solid #8B55DD", boxShadow: "0 0 0 3px rgba(139,85,221,.14)" },
  ok: { border: "1.5px solid #1F9A6B" },
  filled: { border: "1.5px solid rgba(139,85,221,.45)" },
  idle: { border: "1.5px solid rgba(0,0,0,.10)" },
};

export const fieldBox = (state, height) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  height,
  borderRadius: 12,
  background: "#fff",
  boxSizing: "border-box",
  transition: "border-color .2s, box-shadow .2s",
  ...BORDERS[state],
});

export const inputStyle = (padding, roomy) => ({
  width: "100%",
  height: "100%",
  padding,
  boxSizing: "border-box",
  border: "none",
  background: "transparent",
  outline: "none",
  font: `500 ${roomy ? 15.5 : 14.5}px ${FONT}`,
  color: "#171717",
});

export const primaryButton = (enabled, height) => ({
  whiteSpace: "nowrap",
  display: "flex",
  height,
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  gap: 10,
  border: "none",
  borderRadius: 12,
  font: `600 16px ${FONT}`,
  transition: "transform .16s ease, box-shadow .16s ease",
  ...(enabled
    ? { color: "#fff", cursor: "pointer", background: GRADIENT, boxShadow: "0 8px 20px -8px rgba(139,85,221,.75)" }
    : { color: "rgba(255,255,255,.92)", cursor: "not-allowed", background: "#E3C9E6" }),
});

export const labelStyle = { display: "block", marginBottom: 7, color: "#52525B", fontSize: 13.5, fontWeight: 600 };
export const h2Style = { margin: 0, fontSize: 26, fontWeight: 600, color: "#171717", letterSpacing: "-.01em" };
export const subStyle = (margin) => ({ margin, color: "#5C5060", fontSize: 14, fontWeight: 500, textWrap: "pretty" });
export const rise = { animation: "dsRise 420ms cubic-bezier(.22,1,.36,1) both" };

export const Spinner = ({ onLight = false }) => (
  <span
    style={{
      flex: "none",
      width: onLight ? 18 : 17,
      height: onLight ? 18 : 17,
      borderRadius: "50%",
      border: onLight ? "2px solid rgba(139,85,221,.25)" : "2px solid rgba(255,255,255,.35)",
      borderTopColor: onLight ? "#8B55DD" : "#fff",
      boxSizing: "border-box",
      animation: "dsSpin .8s linear infinite",
    }}
  />
);

export const ErrorBanner = ({ children, style }) => (
  <div
    role="alert"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "11px 14px",
      borderRadius: 10,
      background: "rgba(224,71,107,.08)",
      border: "1px solid rgba(224,71,107,.28)",
      ...style,
    }}
  >
    <img src="/error.svg" alt="" style={{ width: 16, height: 16, flex: "none" }} />
    <span style={{ font: `500 13px ${FONT}`, color: "#B4304F" }}>{children}</span>
  </div>
);

export const InfoBanner = ({ children, style }) => (
  <div
    role="status"
    style={{
      padding: "11px 14px",
      borderRadius: 10,
      background: "#F6EEF8",
      border: "1px solid rgba(139,85,221,.28)",
      font: `500 13px ${FONT}`,
      color: "#5B2E8C",
      ...style,
    }}
  >
    {children}
  </div>
);

export const GoogleButtonFace = ({ onClick, loading, short, roomy }) => (
  <button
    type="button"
    className="ds-google-btn"
    onClick={loading ? undefined : onClick}
    style={{
      display: "flex",
      width: "100%",
      height: roomy ? 54 : short ? 46 : 52,
      justifyContent: "center",
      alignItems: "center",
      gap: 10,
      borderRadius: 12,
      border: "1px solid rgba(0,0,0,.10)",
      background: "#fff",
      cursor: loading ? "default" : "pointer",
    }}
  >
    {loading ? <Spinner onLight /> : <img src="/google-icon.svg" alt="" style={{ width: 22, height: 22 }} />}
    <span style={{ color: "#3F3F46", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>
      {loading ? "Connecting to Google…" : "Continue with Google"}
    </span>
  </button>
);
